// Olcha — preview thumbnail for the classic-wine wedding template at
// /templates/olcha.html. Deep burgundy + cream + warm gold; the
// signature is a tiny wax-seal stamp emblem at the centre with the
// couple's initials and a vine-leaf flourish overhead.

import type { LayoutCtx } from "./_shared";

export function OlchaPreviewLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  const [a = names, b = ""] = names.split(/\s*&\s*/);
  const initialA = (a || "S").trim().charAt(0).toUpperCase();
  const initialB = (b || "N").trim().charAt(0).toUpperCase();
  const gold = "#B89968";
  const paper = "#ECDFC8";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: bg,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "10% 8%",
      }}
    >
      {/* Cream paper inset with hairline burgundy frame */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "5.5%",
          background: paper,
          border: `1px solid ${accent}55`,
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "7%",
          border: `0.5px solid ${gold}66`,
          pointerEvents: "none",
        }}
      />

      {/* Vine sprig at top */}
      <svg
        aria-hidden
        viewBox="0 0 80 28"
        style={{ position: "relative", zIndex: 1, width: 64, height: 22, color: gold, marginBottom: 8 }}
      >
        <path d="M2 14 Q 20 4 40 14 Q 60 24 78 14" stroke="currentColor" strokeWidth="0.7" fill="none" opacity="0.8" />
        <circle cx="22" cy="9" r="1.6" fill={accent} />
        <circle cx="26" cy="11" r="1.4" fill={accent} />
        <circle cx="24" cy="13" r="1.2" fill={accent} opacity="0.85" />
        <circle cx="56" cy="17" r="1.6" fill={accent} />
        <circle cx="52" cy="19" r="1.4" fill={accent} />
        <circle cx="54" cy="15" r="1.2" fill={accent} opacity="0.85" />
        <path d="M40 8 L 44 4 L 47 7 L 43 11 Z" fill="currentColor" opacity="0.55" />
      </svg>

      {/* Eyebrow */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          fontFamily: "var(--font-ui)",
          fontSize: 6.5,
          fontWeight: 700,
          letterSpacing: "0.46em",
          color: accent,
          marginBottom: 10,
          textTransform: "uppercase",
        }}
      >
        Anno · MMXXVI
      </div>

      {/* Wax-seal stamp emblem */}
      <div
        aria-hidden
        style={{
          position: "relative",
          zIndex: 1,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: `radial-gradient(circle at 35% 30%, ${accent} 0%, ${accent} 55%, #4A1019 100%)`,
          boxShadow: `inset 0 0 0 1.5px ${gold}, 0 1px 2px rgba(0,0,0,0.35)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: gold,
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 18,
          letterSpacing: "0.04em",
          marginBottom: 14,
        }}
      >
        {initialA}
        <span style={{ fontFamily: "'Pinyon Script', cursive", fontSize: 14, margin: "0 1px" }}>&amp;</span>
        {initialB}
      </div>

      {/* Names — Bodoni Moda italic */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          fontFamily: font,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 26,
          lineHeight: 1.0,
          color: ink,
          letterSpacing: "0.005em",
        }}
      >
        {a}
      </div>
      <div
        style={{
          position: "relative",
          zIndex: 1,
          fontFamily: "'Pinyon Script', cursive",
          fontSize: 22,
          color: gold,
          margin: "4px 0",
          lineHeight: 1,
        }}
      >
        &amp;
      </div>
      <div
        style={{
          position: "relative",
          zIndex: 1,
          fontFamily: font,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 26,
          lineHeight: 1.0,
          color: ink,
        }}
      >
        {b}
      </div>

      {/* Hairline */}
      <div
        aria-hidden
        style={{
          position: "relative",
          zIndex: 1,
          width: "44%",
          height: 1,
          background: gold,
          opacity: 0.6,
          margin: "16px 0 12px",
        }}
      />

      {/* Date */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          fontWeight: 600,
          letterSpacing: "0.34em",
          color: ink,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          position: "relative",
          zIndex: 1,
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 11,
          color: ink,
          opacity: 0.78,
          marginTop: 6,
        }}
      >
        {venue}
      </div>
    </div>
  );
}
